import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { ArticleData, ArticleSection } from '@/mocks/article-data';
import { allArticles } from '@/mocks/article-data';
import { categories } from '@/mocks/categories';

function ArticleNav({ currentSlug }: { currentSlug: string }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const otherArticles = allArticles.filter((a) => a.slug !== currentSlug);

  return (
    <nav className={`relative z-50 w-full transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/60 ${scrolled ? 'fixed top-0' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-[72px]">
        <Link
          to="/"
          className="text-xl md:text-2xl font-heading font-bold text-foreground-900 whitespace-nowrap"
        >
          hipobuyspread<span className="text-accent-400">.org</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
            Home
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Categories
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div ref={guidesRef} className="relative">
            <button
              onClick={() => setGuidesDropdownOpen(!guidesDropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              Guides
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {guidesDropdownOpen && (
              <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 z-50 max-h-80 overflow-y-auto">
                {allArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    className={`block px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${
                      article.slug === currentSlug
                        ? 'text-primary-700 bg-primary-50 font-medium'
                        : 'text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
                    }`}
                    onClick={() => setGuidesDropdownOpen(false)}
                  >
                    {article.heroTitle.replace(' Complete Guide 2026', '')}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            Hipobuy Sheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-900"
        >
          <i className={mobileMenuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 px-4 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <Link to="/" className="flex items-center gap-3 py-3 text-foreground-700 font-medium text-sm">
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100"><i className="ri-home-4-line text-foreground-600"></i></span>
            Home
          </Link>

          <div>
            <button
              onClick={() => setGuidesOpen(!guidesOpen)}
              className="flex items-center justify-between w-full py-3 text-foreground-700 font-medium text-sm"
            >
              <span className="flex items-center gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100"><i className="ri-article-line text-foreground-600"></i></span>
                Guides
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {guidesOpen && (
              <div className="ml-11 space-y-1 pb-2">
                {allArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/${article.slug}`}
                    className={`block py-2 px-3 text-sm rounded-md transition-colors ${
                      article.slug === currentSlug
                        ? 'text-primary-700 bg-primary-50 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {article.heroTitle.replace(' Complete Guide 2026', '')}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] text-foreground-400 font-semibold uppercase tracking-widest px-1 py-2">Product Categories</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 py-2.5 px-2 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-700 rounded-md transition-colors"
                >
                  <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded bg-background-100">
                    <i className="ri-arrow-right-up-line text-[10px] text-foreground-400"></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-background-200/60">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold"
            >
              Buy It Now
            </a>
          </div>

          <div className="pt-1 pb-2">
            <p className="text-[10px] text-foreground-400 font-semibold uppercase tracking-widest px-1 py-2">Other Spreadsheet Guides</p>
            <div className="space-y-1">
              {otherArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/${article.slug}`}
                  className="flex items-center gap-3 py-2.5 px-2 text-sm text-foreground-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded bg-background-100">
                    <i className="ri-guide-line text-xs text-foreground-400"></i>
                  </span>
                  {article.heroTitle.replace(' Complete Guide 2026', '')}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

function ArticleHero({ article }: { article: ArticleData }) {
  return (
    <section className="relative min-h-[50vh] flex flex-col items-center justify-center">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${article.heroImageUrl})`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40" />

      <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-4xl mx-auto py-20">
        <p className="text-accent-200 text-xs md:text-sm font-label tracking-widest uppercase mb-4">
          {article.heroLabel}
        </p>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-4">
          {article.heroTitle}
        </h1>
        <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
          {article.heroSubtitle}
        </p>
        <div className="mt-6 flex items-center justify-center gap-4 text-white/60 text-sm">
          <span>By Hipobuy Editorial Team</span>
          <span className="w-1 h-1 bg-white/30 rounded-full"></span>
          <span>Updated July 2026</span>
          <span className="w-1 h-1 bg-white/30 rounded-full"></span>
          <span>12 min read</span>
        </div>
      </div>
    </section>
  );
}

function SectionRenderer({ section, article }: { section: ArticleSection; article: ArticleData }) {
  switch (section.type) {
    case 'intro':
      return (
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {section.title}
          </h2>
          <div
            className="text-base md:text-lg text-foreground-700 leading-relaxed space-y-5"
            dangerouslySetInnerHTML={{ __html: section.content || '' }}
          />
        </section>
      );

    case 'text':
      return (
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {section.title}
          </h2>
          <div
            className="text-base md:text-lg text-foreground-700 leading-relaxed space-y-5"
            dangerouslySetInnerHTML={{ __html: section.content || '' }}
          />
        </section>
      );

    case 'comparison-table':
      return (
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {section.title}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-foreground-950 text-white">
                  {section.tableHeaders?.map((header) => (
                    <th key={header} className="text-left py-4 px-5 font-label font-semibold whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.tableRows?.map((row, i) => (
                  <tr key={i} className={`border-b border-background-200/60 ${i % 2 === 0 ? '' : 'bg-background-100/50'}`}>
                    {row.map((cell, j) => (
                      <td key={j} className={`py-3.5 px-5 whitespace-nowrap ${j === 0 ? 'font-semibold text-foreground-900' : 'text-foreground-600'}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );

    case 'feature-list':
      return (
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {section.title}
          </h2>
          <div className="space-y-5">
            {section.items?.map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-sm">
                  {i + 1}
                </div>
                <div>
                  <h4 className="text-base font-heading font-semibold text-foreground-900 mb-1">{item.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case 'faq':
      return (
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {section.title}
          </h2>
          <div className="space-y-5">
            {section.items?.map((item, i) => (
              <div key={i} className="bg-background-100/70 border border-background-200/60 rounded-lg p-5">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                  {item.title}
                </h3>
                <div
                  className="text-sm text-foreground-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              </div>
            ))}
          </div>
        </section>
      );

    case 'cta':
      return (
        <section className="mb-16 bg-foreground-950 rounded-lg p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
            {section.title}
          </h2>
          <p className="text-white/70 text-base mb-6 max-w-lg mx-auto">
            {section.content}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="w-full sm:w-auto px-6 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-all"
            >
              Back to Hipobuy Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto px-6 py-3 bg-white/10 text-white border border-white/20 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-white/20 transition-all"
            >
              Visit Main Store
            </a>
          </div>
        </section>
      );

    case 'categories':
      return (
        <section className="mb-10">
          <h3 className="text-xl font-heading font-bold text-foreground-950 mb-5">
            Explore Related Categories
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block bg-background-100/70 border border-background-200/60 rounded-lg px-4 py-3 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-all text-center"
              >
                {cat.name}
              </a>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-background-200/60">
            <p className="text-center text-foreground-500 text-sm font-medium mb-4">
              More Spreadsheet Guides
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {allArticles
                .filter((a) => a.slug !== article.slug)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/${a.slug}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-xs text-foreground-600 hover:text-primary-700 hover:border-primary-300 transition-all"
                  >
                    <i className="ri-article-line text-xs"></i>
                    {a.heroTitle.replace(' Complete Guide 2026', '')}
                  </Link>
                ))}
            </div>
          </div>
          <p className="text-center mt-6">
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm underline">
              ← Back to Hipobuy Spreadsheet Homepage
            </Link>
          </p>
        </section>
      );

    default:
      return null;
  }
}

export default function ArticleLayout({ article }: { article: ArticleData }) {
  return (
    <main>
      <ArticleNav currentSlug={article.slug} />
      <ArticleHero article={article} />
      <article className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          {article.sections.map((section, index) => (
            <SectionRenderer key={index} section={section} article={article} />
          ))}
        </div>
      </article>
    </main>
  );
}